import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  setMessage,
} from '../../store/authSlice';

import {
  saveEditedPaymentCard,
  getGuestByReservationId,
  getPaymentCardImageByReference,
  getPaymentCardBackupByReference,
} from '../../store/widgetSlice';

import { toBase64 } from '../../helpers';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  panel: {
    backgroundColor: theme.palette.background.default,
  },
  status: {
    fontWeight: 600,
  },
  details: {
    maxWidth: 260,
    fontSize: 14,
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  selfie: {
    width: 150,
    height: 150,
    borderRadius: '50%',
    objectFit: 'cover',
    objectPosition: 'center',
  },
  document: {
    width: 200,
    height: 150,
    objectFit: 'cover',
    objectPosition: 'center',
    maxWidth: '100%',
  },
  bold: {
    fontWeight: 600,
  },
  cell: {
    height: 30,
    padding: 0,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),

    'tr:last-child &': {
      borderBottom: 0,
    },
  },
  badge: {
    position: 'absolute',
    top: -theme.spacing(1),
    right: -theme.spacing(1),
    borderRadius: '50%',
    background: theme.palette.background.paper,
  },
  cardImageContainer: {
    width: '450px',
    maxWidth: '100%',
    margin: '0 auto',
  },
  cardImage: {
    width: '100%',
  },
  name: {
    maxWidth: '150px',
    margin: 0,
  },
  cardNumber: {
    maxWidth: '150px',
    margin: 0,
  },
  alignCenter: {
    textAlign: 'center',
  },
}));

export default function CardResults({
  session, editable, compact, admin, showGuestData, onSessionChange,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [image, setImage] = useState('');
  const [file, setFile] = useState(null);
  const [guest, setGuest] = useState(null);
  const [lastSession, setLastSession] = useState({});
  const [cardImage, setCardImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const handleChange = (event) => {
    onSessionChange({ ...session, [event.target.name]: event.target.value });
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleSaveEditedPaymentCard = (event) => {
    if (event) event.preventDefault();
    if (session && session.reference && cardImage) {
      dispatch(saveEditedPaymentCard({ reference: session.reference, cardImage }))
        .unwrap()
        .then((response) => {
          if (response) {
            dispatch(setMessage({ message: 'Card image updated successfully', severity: 'success' }));
          } else {
            dispatch(setMessage({ message: 'Card image could not be updated', severity: 'error' }));
          }
        })
        .catch(() => {
          dispatch(setMessage({ message: 'Card image could not be updated', severity: 'error' }));
        });
    }
  };

  useEffect(() => {
    if (file) {
      toBase64(file)
        .then((data) => {
          const [imageData] = data.split(',').slice(-1) || [];

          setPreviewImage(data);
          setCardImage(imageData);
        })
        .catch(() => {
          dispatch(setMessage({ message: 'File could not be parsed', severity: 'error' }));
        });
    }
  }, [dispatch, file]);

  useEffect(() => {
    if (session && session.reference && (!session.imageBase64 || !session.imageEditBase64)
      && lastSession.reference !== session.reference) {
      if (admin) {
        dispatch(getPaymentCardBackupByReference({ reference: session.reference }))
          .unwrap()
          .then((imageBase64) => {
            if (imageBase64) {
              return Promise.resolve(imageBase64);
            }

            return Promise.reject(new Error());
          })
          .then((imageBase64) => {
            setImage(imageBase64);
            setLastSession(session);
          })
          .catch(() => {
            dispatch(getPaymentCardImageByReference({ reference: session.reference }))
              .unwrap()
              .then((imageBase64) => {
                setImage(imageBase64);
                setLastSession(session);
              })
              .catch(() => {
                setImage('');
                setLastSession(session);
              });
          });
      } else {
        dispatch(getPaymentCardImageByReference({ reference: session.reference }))
          .unwrap()
          .then((imageBase64) => {
            setImage(imageBase64);
            setLastSession(session);
          })
          .catch(() => {
            setImage('');
            setLastSession(session);
          });
      }
    } else if (session && session.imageBase64 && lastSession.reference !== session.reference) {
      setImage(session.imageBase64);
      setLastSession(session);
    } else if (session && session.imageEditBase64 && lastSession.reference !== session.reference) {
      setImage(session.imageEditBase64);
      setLastSession(session);
    } else if (lastSession.reference !== session.reference) {
      setImage('');
      setLastSession(session);
    }
  }, [dispatch, session, admin, lastSession]);

  useEffect(() => {
    if (session && session.reservationId && showGuestData) {
      dispatch(getGuestByReservationId({ reservationId: session.reservationId }))
        .unwrap()
        .then((guestData) => {
          setGuest(guestData);
        })
        .catch(() => {
          setGuest(null);
        });
    } else {
      setGuest(null);
    }
  }, [dispatch, session, showGuestData]);

  return (
    <>
      <Box className={classes.alignCenter} m={3}>
        {
          (image && !previewImage)
            ? (
              <div className={classes.cardImageContainer}>
                <img className={classes.cardImage} src={`data:image/png;base64,${image}`} alt={session.reference} />
              </div>
            )
            : (
              previewImage
              && (
                <div className={classes.cardImageContainer}>
                  <img className={classes.cardImage} src={previewImage} alt={session.reference} />
                </div>
              )
            )
        }
        { editable
          && (
          <>
            <input
              type="file"
              name="file"
              id="file"
              placeholder={null}
              onChange={handleFileChange}
            />
            <button type="submit" onClick={handleSaveEditedPaymentCard}>Upload</button>
          </>
          )}
        <TableContainer className={classes.container}>
          <Table className={classes.table}>
            <TableBody>
              <TableRow className={classes.row}>
                <TableCell className={[classes.cell, classes.bold]} align="left">
                  Reference No.
                </TableCell>
                { editable && (<TableCell className={[classes.cell, classes.bold]} align="left" />)}
                <TableCell className={classes.cell} align="right">
                  {session && session.reference}
                </TableCell>
                <TableCell className={classes.cell} align="right" />
              </TableRow>
              {
                (!compact)
                  ? (
                    <>
                      <TableRow className={classes.row}>
                        <TableCell className={[classes.cell, classes.bold]} align="left">
                          First Name
                        </TableCell>
                        <TableCell className={classes.cell} align="left">
                          { guest && guest.firstName }
                        </TableCell>
                        <TableCell className={classes.cell} align="right">
                          {
                            (editable)
                              ? (
                                <TextField
                                  variant="outlined"
                                  margin="normal"
                                  id="firstName"
                                  name="firstName"
                                  value={session && session.firstName}
                                  onChange={handleChange}
                                  className={classes.name}
                                  size="small"
                                  required
                                />
                              )
                              : (session && session.firstName)
                          }
                        </TableCell>
                      </TableRow>
                      <TableRow className={classes.row}>
                        <TableCell className={[classes.cell, classes.bold]} align="left">
                          Last Name
                        </TableCell>
                        <TableCell className={classes.cell} align="left">
                          { guest && guest.lastName }
                        </TableCell>
                        <TableCell className={classes.cell} align="right">
                          {
                            (editable)
                              ? (
                                <TextField
                                  variant="outlined"
                                  margin="normal"
                                  id="lastName"
                                  name="lastName"
                                  value={session && session.lastName}
                                  onChange={handleChange}
                                  className={classes.name}
                                  size="small"
                                  required
                                />
                              )
                              : (session && session.lastName)
                          }
                        </TableCell>
                      </TableRow>
                      <TableRow className={classes.row}>
                        <TableCell className={[classes.cell, classes.bold]} align="left">
                          Card Network
                        </TableCell>
                        <TableCell className={[classes.cell, classes.bold]} align="left" />
                        <TableCell className={classes.cell} align="right">
                          {
                            (editable)
                              ? (
                                <TextField
                                  variant="outlined"
                                  margin="normal"
                                  id="cardNetwork"
                                  name="cardNetwork"
                                  value={session && session.cardNetwork}
                                  onChange={handleChange}
                                  className={classes.name}
                                  size="small"
                                  required
                                />
                              )
                              : (session && session.cardNetwork)
                          }
                        </TableCell>
                        <TableCell className={classes.cell} align="right" />
                      </TableRow>
                    </>
                  )
                  : <></>
              }
              <TableRow className={classes.row}>
                <TableCell className={[classes.cell, classes.bold]} align="left">
                  Card Number
                </TableCell>
                {editable && (
                  <TableCell className={classes.cell} align="left">
                    { guest && guest.cardNumber }
                  </TableCell>
                )}
                <TableCell className={classes.cell} align="right">
                  {
                      (editable)
                        ? (
                          <TextField
                            variant="outlined"
                            margin="normal"
                            id="cardNumber"
                            name="cardNumber"
                            value={session && session.cardNumber}
                            onChange={handleChange}
                            className={classes.cardNumber}
                            size="small"
                            required
                          />
                        )
                        : (session && session.cardNumber)
                      }
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
}
